import type { FoldFile } from './types';

export interface FoldParseResult {
  file: FoldFile | null;
  errors: string[];
}

/**
 * Parse FOLD JSON. This layer only checks that the document is JSON and that
 * the array-shaped fields really are arrays; semantic checks (index ranges,
 * assignment counts) live in validator.ts so that a partially broken file can
 * still be inspected instead of being rejected outright.
 */
export function parseFold(text: string): FoldParseResult {
  const errors: string[] = [];
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return { file: null, errors: [`Not valid JSON: ${(e as Error).message}`] };
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { file: null, errors: ['A FOLD file must be a JSON object at the top level.'] };
  }
  const obj = raw as Record<string, unknown>;

  const arrayFields = [
    'vertices_coords',
    'edges_vertices',
    'edges_assignment',
    'edges_foldAngle',
    'faces_vertices',
    'file_frames',
    'faceOrders',
  ];
  for (const f of arrayFields) {
    if (f in obj && !Array.isArray(obj[f])) {
      errors.push(`Field "${f}" must be an array; ignoring it.`);
      delete obj[f];
    }
  }

  if (typeof obj.file_spec === 'number' && obj.file_spec > 1.2) {
    errors.push(
      `This file declares FOLD spec ${obj.file_spec}; Fold implements 1.2 and will ignore anything newer.`,
    );
  }

  return { file: obj as FoldFile, errors };
}

/**
 * Resolve a FOLD file down to a single frame's worth of geometry.
 * Frame 0 is the top-level object; frames in `file_frames` may inherit from it.
 */
export function resolveFrame(file: FoldFile, frameIndex = 0): FoldFile {
  if (frameIndex === 0 || !file.file_frames || file.file_frames.length === 0) return file;
  const frame = file.file_frames[frameIndex - 1];
  if (!frame) return file;
  if (frame.frame_inherit) {
    const parentIndex = frame.frame_parent ?? 0;
    const parent = resolveFrame(file, parentIndex);
    return { ...parent, ...frame };
  }
  return frame;
}
